import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take } from 'rxjs';

/**
 * Single-flight coordinator for token refresh. When several API requests fail
 * with `401` at once, only the first triggers a refresh; the rest queue and
 * retry once it resolves. Ported from the legacy `TokenInterceptor`'s
 * `isRefreshing` + `refreshTokenSubject` gate, lifted into a root service so
 * the state has a real injector lifecycle (and resets cleanly between tests)
 * instead of living in module scope.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshCoordinator {
  private isRefreshing = false;
  private readonly refreshed = new BehaviorSubject<boolean>(false);

  /**
   * Run `refresh` at most once across concurrent callers, then `retry`.
   *
   * The first caller runs `refresh`; on success it releases the queue and runs
   * `retry`. Later callers skip the refresh, wait for it to resolve, then run
   * their own `retry`. A failing `refresh` resets the gate and propagates its
   * error to the first caller (which signs out); `retry` errors pass straight
   * through, so a genuine post-refresh failure is not mistaken for a refresh
   * failure.
   */
  run<T>(refresh: () => Observable<unknown>, retry: () => Observable<T>): Observable<T> {
    if (this.isRefreshing) {
      return this.refreshed.pipe(
        filter((done) => done),
        take(1),
        switchMap(() => retry()),
      );
    }

    this.isRefreshing = true;
    this.refreshed.next(false);

    return refresh().pipe(
      catchError((err) => {
        // Refresh itself failed — reset so later 401s aren't wedged in the
        // queue, and let the caller sign out.
        this.isRefreshing = false;
        throw err;
      }),
      switchMap(() => {
        this.isRefreshing = false;
        this.refreshed.next(true);
        return retry();
      }),
    );
  }
}
